<template lang="pug">
	.creative--sub-insertions-generator
		FileUploader(
			v-if="visible",
			ref="uploader",
			:dropZoneHeight="showExcelSettings ? 600 : 500",
			:dropText="creative.JsonConfig.excelData.length ? $tCaption('or drop excel file with speech values here. Also you can select from list:') : $tCaption('Drop excel file with speech values here')",
			:isUIHidden="true",
			:closeOnOutsideClick="false",
			:popupConfig="popupConfig",
			:error-message="fileErrorMessage",
			:accept="['.xls', '.xlsx']",
			:uploadButtonActive="!!keyColumn && !!record && !!record.voice && insertionDetailsVisible",
			:uploadSelectorHidden="insertionDetailsVisible || uploadSelectorHidden || !!hasUploadedFiles",
			:uploadText="$tCaption('Next', '@actions')",
			@remove-files="onRemoveFiles",
			@file-selected="v => onFileSelected(v, true)",
			@popup="onPopup",
			@upload="onGenerate",
		)
			.content
				.sub-content(:class="{ 'disabled-controls': isLoading }")
					.creative--sub-insertions-generator.flex-col.space(v-if="insertionDetailsVisible")
						.flex-row.wrap
							.adt-tab(v-if="tracks.length > 1", v-for="(item, idx) in tracks", :class="{ active: idx === activeTrackIdx }", @click="() => activeTrackIdx = idx") {{$Data.limitText(item.name(), 20)}}
						.flex-row.space-medium
							.flex-col.space.flex-1
								.flex-col.space(v-if="showExcelSettings")
									DxCheckBox(:value.sync="calcGeoLocation", :text="$tCaption('Use address column to calculate location')")
									.font-tiny.grey.upper {{$tCaption('Note, if not enabled, then the lattitude, longtitude and radius columns will be used for targeting.')}}
								.flex-col.space
									DxCheckBox(:value.sync="addToExisting", :text="$tCaption('Add to existing sub-tracks')")
									.font-tiny.grey.upper {{$tCaption('if disabled existing sub-tracks will be removed before uploading')}}
							.right-excel-settings.flex-col.space(v-if="showExcelSettings")
								.flex-col.space.field
									.font-small.flex-row.space.align-center(:class="{ required: !keyColumn }")
										.upper.grey {{$tCaption('Key column')}}
										span(v-if="!keyColumn") {{$tCaption('(required)')}}
									DxSelectBox(:value.sync="keyColumn", :data-source="excelColumns", :validationStatus="!keyColumn ? 'invalid' : 'valid'")
							DxButton(v-if="showExcelSettings", icon="event", :text="$tCaption('Limited period')", @click="showLimitedPeriodPopup", :type="hasLimitedPeriod ? 'default' : 'normal'")
						InsertionDetails(
							ref="insertionDetails",
							:ssmlOnly="true",
							:tracker="tracker",
							:excelDataVisible="excelDataVisible",
							:track="track",
							:item="record",
							:excel-columns="excelColumns",
							:getSsml="editingInsertionCmp.getSsml",
							:customContextMenuItems="excelContextMenuItems",
							:preventStoreParameters="false",
							@loadAudio="editingInsertionCmp.actionGenerateAudio",
							@stop="editingInsertionCmp.stop",
							@change="onRecordChange"
						)
					template(v-else)
						.flex-col.space
							.flex-row.space(v-if="track && track.insertion.JsonConfig.subTracksGenerationLogUrl")
								a(:href="track.insertion.JsonConfig.subTracksGenerationLogUrl") {{$tCaption('Download last unprocessed data')}}
							CreativeExcelData(:creative="creative", :tracker="tracker")
								template(v-slot:actions="{ data }")
									DxButton(:text="$tCaption('Use')", icon="fas fa-arrow-right", type="success", @click="useExcelData(data.file)", :disabled="!!usedExcelData")
				Loader(v-if="isLoading", :fullscreen="true", text="")
		Popup(v-if="limitedPeriodPopupVisible", :popupVisible.sync="limitedPeriodPopupVisible", :width="420", :autosize="true", :title="$tCaption('Limited period')")
			.flex-col.space-medium
				.flex-col.space
					.font-small.upper.grey {{$tCaption('Active from column')}}
					DxSelectBox(:value.sync="limitedPeriod.fromColumn", :data-source="excelColumns", stylingMode="filled", :showClearButton="true")
				.flex-col.space
					.font-small.upper.grey {{$tCaption('Active to column')}}
					DxSelectBox(:value.sync="limitedPeriod.toColumn", :data-source="excelColumns", stylingMode="filled", :showClearButton="true")
				.flex-col.space
					.font-small.upper.grey {{$tCaption('Format')}}
					DxTextBox(:value.sync="limitedPeriod.dateFormat", stylingMode="filled")
			template(slot="actions-left")
				DxButton(:text="$tCaption('Clear', '@actions')", @click="clearLimitedPeriod")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close', '@actions')", @click="limitedPeriodPopupVisible = false")
</template>

<script>
	import _ from 'lodash';
	import FileUploader from 'app-core/FileUploader.vue';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import axios from 'axios';
	import InsertionDetails from 'view/insertion/Detail.vue';
	import ssmlHelper from 'view/insertion/ssml-helper';
	import storage from 'storage';
	import DxSelectBox from 'devextreme-vue/select-box';
	import CreativeExcelData from './CreativeExcelData.vue';
	import Loader from 'app-core/Loader.vue';
	import { readColumns, getUsedColumnsInSsml } from './excel-data-helper';
	import Popup from 'app-core/Popup.vue';

	export default {
		translationCategory: 'creative.Detail',
		name: 'creative--sub-insertions-generator',
		components: {
			DxTextBox, DxButton, FileUploader, InsertionDetails, DxCheckBox, DxSelectBox, CreativeExcelData, Loader, Popup
		},
		props: ['tracker', 'editingInsertionCmp', 'creative'],
		data() {
			return {
				tracks: [],
				locationColumnName: '',
				records: [],
				insertionDetailsVisible: false,
				excelColumns: null,
				excelDataVisible: false,
				fileErrorMessage: null,
				calcGeoLocation: false,
				addToExistingProp: storage.prop('subInsertionsGenerator_addToExisting', void 0, false),
				visible: false,
				activeTrackIdx: 0,
				keyColumn: null,
				usedExcelData: null,
				isLoading: 0,
				selectedFile: null,
				uploadSelectorHidden: false,
				limitedPeriodPopupVisible: false,
				limitedPeriod: {
					fromColumn: null,
					toColumn: null,
					dateFormat: 'M.dd.yy H:mm'
				}
			};
		},
		computed: {
			hasLimitedPeriod() {
				return this.activeFromColumnIndex > 0 || this.activeToToColumnIndex > 0;
			},
			showExcelSettings() {
				return !this.usedExcelData?.columns;
			},
			popupConfig() {
				return { height: this.showExcelSettings ? 710 : 610, width: 1020 };
			},
			keyColumnIndex() {
				return this.excelColumns.findIndex(f => f === this.keyColumn);
			},
			keyColumnIsAddress() {
				return this.keyColumn === 'Address';
			},
			addToExisting: {
				get: function() {
					return this.addToExistingProp.value;
				},
				set: function(v) {
					this.addToExistingProp.value = v;
				}
			},
			track() {
				return this.tracks[this.activeTrackIdx];
			},
			record() {
				return this.records[this.activeTrackIdx];
			},
			excelContextMenuItems() {
				return [
					{	
						text: this.$tCaption('Excel fields'),
						template: 'service',
						__type: 'customFields',
						imageUrl: null,
						icon: 'list',
						items: 
						[
							...(this.excelColumns || []).map(f => ({
								text: f,
								template: 'output',
								icon: 'f',
								insertValue: `[${f}@xls]`,
								allowFieldValue: true
							})),
							...(this.calcGeoLocation ? [
								...['Target address'].map(f => ({
									text: f,
									template: 'output',
									icon: 'f',
									complexType: 'geoplace',
									complexValue: {
										Lon: '[Target_Lon@xls]',
										Lat: '[Target_Lat@xls]',
										PlaceName: '[Target_Name@xls]',
									},
									allowFieldValue: true
								})),
								{
									text: 'Target name',
									template: 'output',
									icon: 'f',
									insertValue: `[Target_Name@xls]`,
									allowFieldValue: true
								}
							] : [])
						]
					}
				];
			},
			hasUploadedFiles() {
				return (this.creative.JsonConfig.excelData || []).length;
			},
			activeFromColumnIndex() {
				return this.excelColumns.findIndex(f => f === this.limitedPeriod.fromColumn);
			},
			activeToColumnIndex() {
				return this.excelColumns.findIndex(f => f === this.limitedPeriod.toColumn);
			},
		},
		mounted() {
			if (__DEBUG__)
				window.subInsertionsGenerator = this;
		},
		methods: {
			clearLimitedPeriod() {
				this.limitedPeriod.fromColumn = null;
				this.limitedPeriod.toColumn = null;
				this.limitedPeriodPopupVisible = false;
			},
			showLimitedPeriodPopup() {
				this.limitedPeriodPopupVisible = false;
				this.$nextTick(() => this.limitedPeriodPopupVisible = true);
			},
			checkUseExistingExcelFile() {
				let excelFile = (this.creative.JsonConfig.excelData || [])[0];
				if (excelFile) {
					this.useExcelData(excelFile);
				}
				this.uploadSelectorHidden = !!excelFile;
			},
			async useExcelData(file) {
				if (__DEBUG__)
					console.debug('Use excel data', { file });
				this.usedExcelData = file;
				this.keyColumn = file.keyColumn;
				this.excelColumns = _.cloneDeep(file.columns);
				this.calcGeoLocation = file.calcGeoLocation;

				if (file?.azureFile) {
					this.isLoading++;
					const blob = await this.$azure.getBlob(file.azureFile.FileName, true, void 0, void 0, file.azureFile.AzureContainer).finally(() => this.isLoading--);
					if (blob) {
						this.onFileSelected([blob]);
					}
				}
			},
			onRecordChange(record) {
				if (__DEBUG__)
					console.debug('Record changed', { record });
				const rec = this.records.find(f => f.oid === record.oid);
				_.assign(rec, record);
			},
			onPopup(v) {
				this.$refs.tracker?.setTracksActive(!v);
				this.$emit('popup', v);
			},
			showPopup(tracks) {
				this.tracks = tracks;
				this.visible = false;
				this.activeTrackIdx = 0;
				this.records = tracks.map(f => ({ ...this.$Data.formatObjectToLowerCase(f.insertion), ssml: f.insertion?.JsonConfig?.subTracksGenerationSsmlTemplate || f.insertion?.Ssml }));
				this.usedExcelData = null;
				this.insertionDetailsVisible = false;
				this.limitedPeriod.fromColumn = null;
				this.limitedPeriod.toColumn = null;
				this.isLoading++;
				setImmediate(() => {
					this.visible = true;
					this.insertionDetailsVisible = false;
					this.calcGeoLocation = tracks.some(f => f.insertion?.JsonConfig?.subTracksGenerationSettings?.calcGeoLocation);

					if (!this.creative.JsonConfig.allowMultipleExcelData || this.hasUploadedFiles === 1) {
						this.checkUseExistingExcelFile();
					}

					this.$nextTick(() => {
						this.$refs.uploader?.showPopup();
						this.isLoading--;
					});
				});
			},
			onRemoveFiles(files) {
				this.insertionDetailsVisible = files.length > 0;
				this.excelColumns = null;
				this.excelDataVisible = false;
				this.fileErrorMessage = null;
			},
			onFileSelected(files, checkExistsInExcel) {
				let process = async () => {
					if (files.length > 0) {
						let file = files[0];
						this.selectedFile = file;
						this.fileErrorMessage = null;

						this.isLoading++;
						this.excelColumns = await readColumns(file).finally(() => this.isLoading--);

						if (this.excelColumns?.length) {
							this.excelDataVisible = !!this.excelColumns.length;
							this.insertionDetailsVisible = true;
							if (!this.keyColumn) {
								this.keyColumn = this.tracks?.map(f => f.insertion?.JsonConfig?.subTracksGenerationSettings?.keyColumn).filter(f => f)[0];
							}
							if (!this.keyColumn || !this.excelColumns.includes(this.keyColumn)) {
								this.keyColumn = this.excelColumns.find(f => f === 'Address');
							}

							let usedColumns = [];
							this.tracks.map(f => {
								if (f?.insertion?.JsonConfig?.subTracksGenerationSsmlTemplate) {
									getUsedColumnsInSsml(f.insertion.JsonConfig.subTracksGenerationSsmlTemplate, usedColumns);
								}
							});

							let notExistsColumns = usedColumns.filter(f => !this.excelColumns.includes(f));
							if (notExistsColumns.length > 0) {
								this.$confirm(void 0, this.$tCaption(`Some used columns are absent in the new excel file: ${notExistsColumns.join(', ')}. Do you want to continue?`)).catch(() => {
									this.$refs.uploader.removeFile(0);
									this.usedExcelData = null;
								});
							}

						} else {
							this.fileErrorMessage = this.$tCaption('The selected excel file must not contain blank lines at the beginning!');
						}
					}
				}
				if (!checkExistsInExcel) {
					process();
				} else {
					const existsExcelData = this.creative.JsonConfig.excelData.find(f => f.name === files[0]?.name);
					if (existsExcelData) {
						this.$confirmMessage({
							title: this.$tCaption('Upload duplicate?'), 
							text: 
								this.$tCaption('This creative already contains an excel file with this name. If you continue, both Excel Files will be available in the creative.') + '<br><br>'
								+ this.$tCaption('Press <b>Continue to upload a duplicate</b>, press <b>No</b> to use the existing excel or press Cancel.'),							
							buttons: [{
								text: this.$tCaption('Continue', '@actions'),
								onClick: () => {
									process();
									return true;
								}
							}, {
								text: this.$tCaption('No, use existing'),
								onClick: () => {
									this.useExcelData(existsExcelData);
									return true;
								},
								type: 'success'
							}, {
								text: 'Cancel',
								onClick: () => {
									if (this.$refs.uploader?.files?.length) {
										this.$refs.uploader.files = [];
									}
									return false;
								},
								type: 'normal'
							}]
						});
					} else {
						process();
					}
				}
			},
			async onGenerate() {
				const tracks = [...this.tracks];
				let records = [...this.records];
				this.$emit('started', tracks);
				let file = this.selectedFile;
				let excelData = this.usedExcelData;

				if (__DEBUG__)
					console.debug('On excel data generate sub-insertions', { file });
				const data = new FormData();

				const importIntoInsertions = records.map(f => ({
					InsertionId: f.oid,
					SsmlTemplate: ssmlHelper.prepareSsml(
						f.ssml,
						{
							...this.$Data.formatObjectToLowerCase(f),
							sender: this,
							isPreview: false
						}
					)?.ssml
				}));

				data.append('importIntoInsertions', JSON.stringify(importIntoInsertions));
				data.append('excelFile', file);
				data.append('calcGeoLocation', excelData?.calcGeoLocation != null ? excelData.calcGeoLocation : this.calcGeoLocation);
				data.append('keyColumn', excelData?.keyColumn != null ? excelData.keyColumn : this.keyColumn);
				data.append('keyColumnIndex', excelData?.keyColumnIndex != null ? excelData.keyColumnIndex : this.keyColumnIndex);
				data.append('addToExisting', this.addToExisting);

				if (this.activeFromColumnIndex > -1 || excelData?.activeFromColumnIndex) {
					data.append('activeFromColumnIndex', excelData?.activeFromColumnIndex != null ? excelData?.activeFromColumnIndex : this.activeFromColumnIndex);
				}

				if (this.activeToColumnIndex > -1 || excelData?.activeToColumnIndex) {
					data.append('activeToColumnIndex', excelData?.activeToColumnIndex != null ? excelData?.activeToColumnIndex : this.activeToColumnIndex);
				}

				if (this.activeFromColumnIndex > -1 || excelData?.activeFromColumnIndex || this.activeToColumnIndex > -1 || excelData?.activeToColumnIndex) {
					data.append('dateFormat', excelData?.dateFormat != null ? excelData.dateFormat : this.limitedPeriod.dateFormat);
				}

				if (!excelData) {
					const azureFile = await this.$azure.uploadBlobWithInfoResponse(`creative_excel_data_${this.$Data.uuid()}.xlsx`, file);

					const info = {
						id: this.$Data.uuid(),
						name: file.name,
						created: new Date(),
						insertions: [],
						azureFile,
						columns: _.cloneDeep(this.excelColumns),
						keyColumn: this.keyColumn,
						keyColumnIndex: this.keyColumnIndex,
						activeFromColumnIndex: this.activeFromColumnIndex,
						activeToColumnIndex: this.activeToColumnIndex,
						calcGeoLocation: this.calcGeoLocation,
						dateFormat: this.limitedPeriod.dateFormat
					};

					this.creative.JsonConfig.excelData.push(info);

					excelData = info;
				}

				if (__DEBUG__)
					console.debug('Used exel data', { data: excelData });

				this.$notifyMessage(
					this.$tCaption('The sub tracks you have uploaded are being processed. It will take some time.'),
					{ busy: false, type: 'warning' }
				);

				tracks.map(f => {
					f.__isLoading++;
					f.__isGeneratingSubInsertions = true;
				});
				
				let subTracksGenerationSettings = {
					keyColumn: excelData?.keyColumn != null ? excelData.keyColumn : this.keyColumn,
					keyColumnIndex: excelData?.keyColumnIndex != null ? excelData.keyColumnIndex : this.keyColumnIndex,
					calcGeoLocation: excelData?.calcGeoLocation != null ? excelData.calcGeoLocation : this.calcGeoLocation,
					excelDataId: excelData?.id,
					activeFromColumnIndex: excelData?.activeFromColumnIndex != null ? excelData.activeFromColumnIndex : this.activeFromColumnIndex,
					activeToColumnIndex: excelData?.activeToColumnIndex != null ? excelData.activeToColumnIndex : this.activeToColumnIndex,
					dateFormat: excelData?.dateFormat != null ? excelData.dateFormat : this.limitedPeriod.dateFormat
				}
				return axios
					.post(
						this.$Data.getMethodUrl('postExternalApi?method=Creative/GenerateSubInsertions'),
						data,
						{
							headers: {
								'Content-Type': 'multipart/form-data',
								authToken: this.$auth.accessToken(),
								...__HEADERS__
							}
						})
					.then(({ data }) => {
						if (__DEBUG__)
							console.debug('GenerateSubInsertions result', { data });

						if (data.Success && data.Data) {
							tracks.map(track => {
								track.insertion.LastProcessingJob = {
									Oid: data.Data,
									Status: 0
								}
							});
						} else if (data.Success === false) {
							throw(data.ErrorMessage);
						}

						return data;
					})
					.catch((ex) => {
						this.$notifyMessage(ex, { fullScreen: true });
						tracks.map(f => {
							f.__isLoading--;
							f.__isGeneratingSubInsertions = false;
						});
						return false;
					})
					.finally(() => {
						if (excelData) {
							records.map(r => {
								let insertionSettings = excelData.insertions.find(f => f.insertionId === r.oid);
								if (!insertionSettings) {
									insertionSettings = {
										insertionId: r.oid
									};
									excelData.insertions.push(insertionSettings);
								}
								this.$set(insertionSettings, 'calcGeoLocation', subTracksGenerationSettings.calcGeoLocation);
								this.$set(insertionSettings, 'keyColumn', subTracksGenerationSettings.keyColumn);
								this.$set(insertionSettings, 'keyColumnIndex', subTracksGenerationSettings.keyColumnIndex);
								this.$set(insertionSettings, 'addToExisting', this.addToExisting);
								this.$set(insertionSettings, 'activeFromColumnIndex', subTracksGenerationSettings.activeFromColumnIndex);
								this.$set(insertionSettings, 'activeToColumnIndex', subTracksGenerationSettings.activeToColumnIndex);
								this.$set(insertionSettings, 'dateFormat', subTracksGenerationSettings.dateFormat);
							});

							if (!excelData.columns || !excelData.keyColumn) {
								this.$set(excelData, 'calcGeoLocation', subTracksGenerationSettings.calcGeoLocation);
								this.$set(excelData, 'keyColumn', subTracksGenerationSettings.keyColumn);
								this.$set(excelData, 'keyColumnIndex', subTracksGenerationSettings.keyColumnIndex);
								this.$set(excelData, 'columns', _.cloneDeep(this.excelColumns));
								this.$set(excelData, 'activeFromColumnIndex', subTracksGenerationSettings.activeFromColumnIndex);
								this.$set(excelData, 'activeToColumnIndex', subTracksGenerationSettings.activeToColumnIndex);
								this.$set(excelData, 'dateFormat', subTracksGenerationSettings.dateFormat);
							}
						}

						tracks.map((track, idx) => {
							this.$set(track.insertion.JsonConfig, 'subTracksGenerationSsmlTemplate', ssmlHelper.prepareSsml(
								records[idx]?.ssml,
								{
									...records[idx],
									sender: this,
									isPreview: false
								}
							)?.ssml)
							this.$set(track.insertion.JsonConfig, 'subTracksGenerationSettings', subTracksGenerationSettings);
							track.__saveInsertion();
						});
					});
			}
		}
	};
</script>
<style lang='scss'>
	.creative--sub-insertions-generator {
		.right-excel-settings {
			min-width: 260px;
		}
		
		.required {
			color: red;
		}
	}
</style>
